if (!customElements.get('brands-carousel')) {
    customElements.define(
        'brands-carousel',
        class BrandsCarousel extends HTMLElement {
            constructor() {
                super();
            }

            connectedCallback() {
                this.selectors = {
                    sliderWrapper: '.brands__carousel-inner',
                    nextEl: `#section-${this.dataset.sectionId} .swiper-button-next`,
                    prevEl: `#section-${this.dataset.sectionId} .swiper-button-prev`,
                    items: '.brands__carousel-item'
                };
                this.classes = {
                    grid: 'f-grid',
                    swiper: 'swiper',
                    swiperWrapper: 'swiper-wrapper',
                    swiperSlide: 'swiper-slide',
                    mobileStrip: 'brands__carousel--mobile-strip'
                };

                this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

                this.items = this.querySelectorAll(this.selectors.items);
                this.sliderInstance = false;

                this.mqlMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile);
                this.isMobile = this.getIsMobile();
                this.mqlMobile.onchange = this.handleBreakpointChange.bind(this);

                this.disableSwiper = this.dataset.disableSwiper === 'true';

                if (this.disableSwiper) {
                    return;
                }

                this.init();
            }

            disconnectedCallback() {
                if (this.mqlMobile) this.mqlMobile.onchange = null;
                this.destroySlider();
            }

            getIsMobile() {
                return FoxTheme.config.mqlMobile?.matches || window.innerWidth < 768;
            }

            handleBreakpointChange() {
                const newIsMobile = this.getIsMobile();

                if (this.isMobile !== newIsMobile) {
                    this.isMobile = newIsMobile;
                    this.destroySlider();
                    this.init();
                }
            }

            init() {
                this.classList.toggle(this.classes.mobileStrip, this.isMobile);
                this.groupItemsIntoSlides();
                this.initSlider();
            }

            getItemsPerSlide() {
                return this.isMobile ? 1 : 14;
            }

            groupItemsIntoSlides() {
                const items = Array.from(this.items);
                if (items.length === 0) {
                    return;
                }

                const existingSlides = this.sliderWrapper.querySelectorAll(`.${this.classes.swiperSlide}`);
                if (existingSlides.length > 0) {
                    existingSlides.forEach(slide => {
                        const slideItems = Array.from(slide.children);
                        slideItems.forEach(item => {
                            this.sliderWrapper.appendChild(item);
                        });
                        slide.remove();
                    });
                }

                const allItems = Array.from(this.sliderWrapper.children);
                const itemsPerSlide = this.getItemsPerSlide();

                this.sliderWrapper.innerHTML = '';
                const slides = this.chunkArray(allItems, itemsPerSlide);

                slides.forEach((slideItems) => {
                    const slide = document.createElement('div');
                    slide.className = `${this.classes.swiperSlide}`;

                    slideItems.forEach(item => {
                        slide.appendChild(item);
                    });

                    this.sliderWrapper.appendChild(slide);
                });
            }

            chunkArray(array, size) {
                const chunks = [];
                for (let i = 0; i < array.length; i += size) {
                    chunks.push(array.slice(i, i + size));
                }

                return chunks;
            }

            getMobileCarouselInsetPx() {
                const section = document.getElementById(`section-${this.dataset.sectionId}`);
                if (!section) return 0;
                const header = section.querySelector('.brands__header');
                if (!header) return 0;
                const cs = getComputedStyle(header);
                const start = cs.paddingInlineStart || cs.paddingLeft;
                return parseFloat(start) || 0;
            }

            getMobileSlidesPerView() {
                const raw = this.dataset.mobileSlidesPerView;
                if (raw == null || raw === '') return 3.5;
                const n = parseFloat(raw);
                return Number.isFinite(n) ? n : 3.5;
            }

            buildSliderOptions() {
                const navigation = {
                    nextEl: document.querySelector(this.selectors.nextEl),
                    prevEl: document.querySelector(this.selectors.prevEl),
                };

                if (this.isMobile) {
                    const inset = this.getMobileCarouselInsetPx();
                    return {
                        slidesPerView: this.getMobileSlidesPerView(),
                        spaceBetween: 12,
                        slidesOffsetBefore: inset,
                        slidesOffsetAfter: inset,
                        centeredSlides: false,
                        slidesPerGroup: 1,
                        navigation,
                        loop: false,
                        pagination: false,
                        threshold: 2,
                        roundLengths: true,
                        watchOverflow: true,
                        watchSlidesProgress: true,
                        grabCursor: true,
                        mousewheel: {
                            enabled: true,
                            forceToAxis: true,
                            releaseOnEdges: true,
                        },
                    };
                }

                return {
                    slidesPerView: 1,
                    spaceBetween: 0,
                    navigation,
                    loop: false,
                    pagination: false,
                };
            }

            initSlider() {
                if (typeof this.sliderInstance === 'object') return;

                this.sliderOptions = this.buildSliderOptions();

                this.classList.add(this.classes.swiper);
                this.sliderWrapper.classList.remove(this.classes.grid);
                this.sliderWrapper.classList.add(this.classes.swiperWrapper);

                const extraModules = this.isMobile ? [window.FoxTheme.Swiper.Mousewheel] : null;

                this.sliderInstance = extraModules?.length
                    ? new window.FoxTheme.Carousel(this, this.sliderOptions, extraModules)
                    : new window.FoxTheme.Carousel(this, this.sliderOptions);

                this.sliderInstance.init();

                if (this.isMobile && this.sliderInstance.slider) {
                    requestAnimationFrame(() => {
                        this.sliderInstance.slider.update();
                    });
                }
            }

            destroySlider() {
                this.restoreOriginalStructure();

                this.classList.remove(this.classes.swiper, this.classes.mobileStrip);
                this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
                this.sliderWrapper.classList.add(this.classes.grid);

                if (typeof this.sliderInstance !== 'object') return;
                this.sliderInstance.slider.destroy();
                this.sliderInstance = false;
            }

            restoreOriginalStructure() {
                const slides = this.sliderWrapper.querySelectorAll(`.${this.classes.swiperSlide}`);

                slides.forEach(slide => {
                    const items = Array.from(slide.children);
                    items.forEach(item => {
                        this.sliderWrapper.appendChild(item);
                    });
                    slide.remove();
                });
            }
        }
    );
}
